import { useEffect, useLayoutEffect, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import type { SeriesTile } from "../../api/types";
import { fmtTick } from "../../components/ui";

export const AXIS_W = 124;
export const SYNC_KEY = "rs-analysis";

/** A single instant (e.g. the live "now" cursor). */
export interface Marker {
  t: number;
  label: string;
  color?: string;
}
/** A captured snapshot window: the seconds a report was actually built from. */
export interface Window {
  start: number;
  end: number;
  label: string;
  color?: string;
}

export const WINDOW_COLOR = "#f97316";

export interface ChartDecorations {
  markers: Marker[];
  windows: Window[];
  pinned: number | null;
  /** [start, end] highlight (e.g. hovered lane interval). */
  range: [number, number] | null;
}

interface Props {
  tile: SeriesTile;
  total: number;
  color: string;
  height?: number;
  theme: "dark" | "light";
  deco: ChartDecorations;
  onHover: (t: number | null) => void;
  onPin: (t: number) => void;
  onPlotBox?: (box: { left: number; width: number }) => void;
  onRemove?: () => void;
}

export function ParamChart({ tile, total, color, height = 140, theme, deco, onHover, onPin, onPlotBox, onRemove }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const plot = useRef<uPlot | null>(null);
  const decoRef = useRef(deco);
  decoRef.current = deco;
  const hoverRef = useRef(onHover);
  hoverRef.current = onHover;
  const pinRef = useRef(onPin);
  pinRef.current = onPin;
  const boxRef = useRef(onPlotBox);
  boxRef.current = onPlotBox;
  const totalRef = useRef(total);
  totalRef.current = total;
  const [value, setValue] = useState<string>("");

  const fg = theme === "dark" ? "#8b93a7" : "#5b6270";
  const grid = theme === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";

  useLayoutEffect(() => {
    const el = host.current;
    if (!el) return;
    const data: uPlot.AlignedData = [tile.t, tile.v as (number | null)[]];
    const opts: uPlot.Options = {
      width: el.clientWidth || 600,
      height,
      padding: [8, 12, 0, 0],
      scales: { x: { time: false, range: () => [0, Math.max(totalRef.current, 1)] } },
      series: [
        {},
        {
          label: tile.mnemonic,
          stroke: color,
          width: 1.2,
          spanGaps: false,
          points: { show: false },
        },
      ],
      axes: [
        { stroke: fg, grid: { stroke: grid }, ticks: { stroke: grid }, values: (_u, vals) => vals.map((v) => fmtTick(v)), size: 24 },
        { stroke: fg, grid: { stroke: grid }, ticks: { stroke: grid }, size: AXIS_W },
      ],
      legend: { show: false },
      cursor: {
        sync: { key: SYNC_KEY, setSeries: false, scales: ["x", null] },
        drag: { x: false, y: false },
        points: { size: 6 },
        y: false,
      },
      hooks: {
        setCursor: [
          (u) => {
            const left = u.cursor.left ?? -1;
            const idx = u.cursor.idx;
            if (left < 0) {
              hoverRef.current(null);
              setValue("");
              return;
            }
            const t = u.posToVal(left, "x");
            hoverRef.current(t);
            if (idx != null && idx >= 0) {
              const v = (u.data[1] as (number | null)[])[idx];
              setValue(v == null ? "—" : formatNum(v));
            }
          },
        ],
        draw: [(u) => drawDecorations(u, decoRef.current, theme)],
        setSize: [
          (u) => {
            const dpr = devicePixelRatio || 1;
            boxRef.current?.({ left: u.bbox.left / dpr, width: u.bbox.width / dpr });
          },
        ],
      },
    };
    const u = new uPlot(opts, data, el);
    plot.current = u;
    const dpr = devicePixelRatio || 1;
    boxRef.current?.({ left: u.bbox.left / dpr, width: u.bbox.width / dpr });
    const onClick = (e: MouseEvent) => {
      const rect = u.over.getBoundingClientRect();
      const x = e.clientX - rect.left;
      if (x < 0 || x > rect.width) return;
      pinRef.current(u.posToVal(x, "x"));
    };
    u.over.addEventListener("click", onClick);
    const ro = new ResizeObserver(() => {
      if (el.clientWidth) u.setSize({ width: el.clientWidth, height });
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      u.over.removeEventListener("click", onClick);
      u.destroy();
      plot.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile.mnemonic, color, height, theme]);

  // live updates: swap data / x-range in place instead of rebuilding the plot
  useEffect(() => {
    const u = plot.current;
    if (!u) return;
    u.setData([tile.t, tile.v as (number | null)[]], false);
    u.setScale("x", { min: 0, max: Math.max(total, 1) });
  }, [tile, total]);

  useEffect(() => {
    plot.current?.redraw(false, false);
  }, [deco]);

  return (
    <div className="pchart">
      <div className="pchart-head" style={{ width: AXIS_W }}>
        <span className="swatch" style={{ background: color }} />
        <span className="mono name" title={tile.units ?? ""}>
          {tile.mnemonic}
        </span>
        <span className="mono val">{value}</span>
        <span className="faint small">
          {tile.units ?? ""} {tile.reduced ? `· ${tile.total}→${tile.samples}` : ""}
        </span>
        {onRemove ? (
          <button className="btn ghost sm rm" onClick={onRemove} title="remove">
            ✕
          </button>
        ) : null}
      </div>
      <div ref={host} />
    </div>
  );
}

function withAlpha(color: string, alpha: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(color.trim());
  if (!m) return color;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
}

function formatNum(v: number): string {
  if (Number.isInteger(v)) return String(v);
  const a = Math.abs(v);
  return v.toFixed(a >= 100 ? 1 : a >= 1 ? 2 : 4);
}

function drawDecorations(u: uPlot, deco: ChartDecorations, theme: "dark" | "light") {
  const ctx = u.ctx;
  const { left, top, width, height } = u.bbox;
  const dpr = devicePixelRatio || 1;
  const x = (t: number) => Math.round(u.valToPos(t, "x", true));
  ctx.save();
  ctx.beginPath();
  ctx.rect(left, top, width, height);
  ctx.clip();

  // snapshot windows: a translucent shade over the captured seconds, no edges or labels
  // (the snapshot chips and the snapshots lane act as the legend). Very short windows are
  // widened to a few pixels and drawn denser so they stay visible.
  for (const w of deco.windows) {
    const color = w.color ?? WINDOW_COLOR;
    const x0 = x(w.start);
    const natural = x(w.end + 1) - x0; // +1: window end is inclusive
    const minW = 3 * dpr;
    const narrow = natural < minW;
    const bw = narrow ? minW : natural;
    const alpha = narrow ? 0.55 : theme === "dark" ? 0.22 : 0.18;
    ctx.fillStyle = withAlpha(color, alpha);
    ctx.fillRect(narrow ? x0 - (minW - natural) / 2 : x0, top, bw, height);
  }
  if (deco.range) {
    const x0 = x(deco.range[0]);
    const x1 = x(deco.range[1]);
    ctx.fillStyle = theme === "dark" ? "rgba(250,204,21,0.16)" : "rgba(202,138,4,0.14)";
    ctx.fillRect(x0, top, Math.max(x1 - x0, 1), height);
  }
  for (const m of deco.markers) {
    const xm = x(m.t);
    const markerColor = m.color ?? (theme === "dark" ? "#cbd5e1" : "#475569");
    ctx.strokeStyle = markerColor;
    ctx.lineWidth = 1.5 * dpr;
    ctx.setLineDash([4 * dpr, 3 * dpr]);
    ctx.beginPath();
    ctx.moveTo(xm, top);
    ctx.lineTo(xm, top + height);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = markerColor;
    ctx.beginPath();
    ctx.moveTo(xm, top);
    ctx.lineTo(xm - 5 * dpr, top + 8 * dpr);
    ctx.lineTo(xm + 5 * dpr, top + 8 * dpr);
    ctx.closePath();
    ctx.fill();
  }
  if (deco.pinned != null) {
    const xp = x(deco.pinned);
    ctx.strokeStyle = theme === "dark" ? "#e5e7eb" : "#111827";
    ctx.lineWidth = 1 * dpr;
    ctx.beginPath();
    ctx.moveTo(xp, top);
    ctx.lineTo(xp, top + height);
    ctx.stroke();
  }
  ctx.restore();
}
